import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  BuildingOfficeIcon,
  StarIcon,
  MapPinIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

interface Amenity {
  name: string;
  icon: string;
}

interface Testimonial {
  customerName: string;
  rating: number;
  comment: string;
  date: string;
}

interface Destination {
  _id: string;
  name: string;
  image?: string;
}

interface Resort {
  _id: string;
  name: string;
  bannerImage: string;
  destination: {
    _id: string;
    name: string;
  };
  about: string;
  amenities: Amenity[];
  gallery: string[];
  testimonials: Testimonial[];
  location: {
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
  contactInfo: {
    phone: string;
    email: string;
    website: string;
  };
  isActive: boolean;
  isPartnerHotel: boolean;
  resortCode: string;
  slug?: string;
  packageCount: number;
  averageRating: number;
  createdAt: string;
}

const AdminResorts: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingResort, setEditingResort] = useState<Resort | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterDestination, setFilterDestination] = useState<string>('all');
  
  // Form state for repeater fields
  const [amenities, setAmenities] = useState<Amenity[]>([{ name: '', icon: '' }]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([{ customerName: '', rating: 5, comment: '', date: new Date().toISOString().split('T')[0] }]);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [existingGallery, setExistingGallery] = useState<string[]>([]); // Track existing gallery URLs
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Data from API - remove mock data when real API is connected
  const [resorts, setResorts] = useState<Resort[]>([]);
  const [loading, setLoading] = useState(true);
  const [destinations, setDestinations] = useState<Destination[]>([]);

  // Google Maps / Location state
  const GOOGLE_MAPS_API_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string) || 'AIzaSyA6njPFHRLiKVAn7_6bWt1d_8sseEcv_Gg';
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [locAddress, setLocAddress] = useState<string>('');
  const [locLat, setLocLat] = useState<number | ''>('');
  const [locLng, setLocLng] = useState<number | ''>('');

  // Fetch resorts from API
  const fetchResorts = async () => {
    try {
      setLoading(true);
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch('/api/resorts?includeInactive=true', { headers });
      if (response.ok) {
        const data = await response.json();
        const resortsData = data.resorts || data;
        setResorts(Array.isArray(resortsData) ? resortsData : []);
      }
    } catch (error) {
      console.error('Error fetching resorts:', error);
      setResorts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch destinations from API for dropdowns/filters
  const fetchDestinations = async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch('/api/destinations', { headers });
      if (response.ok) {
        const data = await response.json();
        const dests = data.destinations || data;
        setDestinations(Array.isArray(dests) ? dests : []);
      } else {
        setDestinations([]);
      }
    } catch (error) {
      console.error('Error fetching destinations:', error);
      setDestinations([]);
    }
  };

  useEffect(() => {
    fetchResorts();
    fetchDestinations();
  }, [token]);

  // Load Google Maps Places script and bind Autocomplete when modal is open
  useEffect(() => {
    if (!showModal) return;

    const loadScript = () => new Promise<void>((resolve) => {
      if ((window as any).google?.maps?.places) return resolve();
      const existing = document.getElementById('google-maps-js');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        return;
      }
      const s = document.createElement('script');
      s.id = 'google-maps-js';
      s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      document.body.appendChild(s);
    });

    const initAutocomplete = () => {
      if (!addressInputRef.current || !(window as any).google?.maps?.places) return;
      const ac = new (window as any).google.maps.places.Autocomplete(addressInputRef.current, { types: ['geocode'] });
      autocompleteRef.current = ac;
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        const addr = place?.formatted_address || addressInputRef.current?.value || '';
        const loc = place?.geometry?.location;
        if (loc) {
          setLocLat(loc.lat());
          setLocLng(loc.lng());
        }
        setLocAddress(addr);
      });
    };

    loadScript().then(initAutocomplete);
  }, [showModal, GOOGLE_MAPS_API_KEY]);


  const filteredResorts = resorts.filter(resort => {
    const statusMatch = filterStatus === 'all' || 
      (filterStatus === 'active' && resort.isActive) ||
      (filterStatus === 'inactive' && !resort.isActive);
    
    const destinationMatch = filterDestination === 'all' || 
      resort.destination._id === filterDestination;

    return statusMatch && destinationMatch;
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const formEl = e.target as HTMLFormElement;
    const raw = new FormData(formEl);
    
    try {
      const fd = new FormData();
      fd.append('name', (raw.get('name') as string) || '');
      fd.append('resortCode', (raw.get('resortCode') as string) || '');
      fd.append('destination', (raw.get('destination') as string) || '');
      fd.append('about', (raw.get('about') as string) || '');
      fd.append('isActive', raw.get('isActive') ? 'true' : 'false');
      fd.append('isPartnerHotel', raw.get('isPartnerHotel') ? 'true' : 'false');

      const filteredAmenities = amenities.filter(a => a.name && a.icon);
      const filteredTestimonials = testimonials.filter(t => t.customerName && t.comment);
      fd.append('amenities', JSON.stringify(filteredAmenities));
      fd.append('testimonials', JSON.stringify(filteredTestimonials));

      // Append location payload if present
      const latNum = typeof locLat === 'string' ? parseFloat(locLat) : locLat;
      const lngNum = typeof locLng === 'string' ? parseFloat(locLng) : locLng;
      const locationPayload: any = { address: locAddress || '' };
      if (typeof latNum === 'number' && typeof lngNum === 'number' && !Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
        locationPayload.coordinates = { latitude: latNum, longitude: lngNum };
      }
      fd.append('location', JSON.stringify(locationPayload));

      if (bannerFile) {
        fd.append('bannerImage', bannerFile);
      } else if (editingResort?.bannerImage) {
        // keep existing banner when updating and no new file selected
        fd.append('bannerImage', editingResort.bannerImage);
      }

      // Handle gallery: send new files + remaining existing URLs
      if (galleryFiles.length) {
        galleryFiles.forEach(file => fd.append('gallery', file));
        console.log('📤 Sending new gallery files:', galleryFiles.length);
      }
      // Always send existing gallery URLs (even if empty - to allow removal of all images)
      if (editingResort) {
        console.log('📤 Sending existing gallery URLs:', existingGallery.length, existingGallery);
        fd.append('existingGallery', JSON.stringify(existingGallery));
      }

      let response;
      const headers: HeadersInit = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (editingResort) {
        response = await fetch(`/api/resorts/${editingResort._id}`, {
          method: 'PUT',
          headers,
          body: fd
        });
      } else {
        response = await fetch('/api/resorts', {
          method: 'POST',
          headers,
          body: fd
        });
      }

      if (response.ok) {
        // Reset form-specific file states and refresh list
        setBannerFile(null);
        setGalleryFiles([]);
        setExistingGallery([]);
        fetchResorts();
        setShowModal(false);
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to save resort');
      }
    } catch (error) {
      console.error('Error saving resort:', error);
      alert('Failed to save resort');
    }
  };

  const handleAdd = () => {
    setEditingResort(null);
    setAmenities([{ name: '', icon: '' }]);
    setBannerFile(null);
    setGalleryFiles([]);
    setExistingGallery([]);
    setTestimonials([{ customerName: '', rating: 5, comment: '', date: new Date().toISOString().split('T')[0] }]);
    setShowModal(true);
    setLocAddress('');
    setLocLat('');
    setLocLng('');
  };

  const handleEdit = (resort: Resort) => {
    setEditingResort(resort);
    setAmenities(resort.amenities?.length ? resort.amenities : [{ name: '', icon: '' }]);
    setBannerFile(null);
    setGalleryFiles([]);
    const galleryImages = resort.gallery || [];
    console.log('📝 Editing resort:', resort.name);
    console.log('📸 Loading gallery images:', galleryImages.length, galleryImages);
    setExistingGallery(galleryImages); // Load existing gallery images
    setTestimonials(resort.testimonials?.length ? resort.testimonials : [{ customerName: '', rating: 5, comment: '', date: new Date().toISOString().split('T')[0] }]);
    setShowModal(true);
    // Initialize location fields from existing data
    const coords: any = (resort as any).location?.coordinates;
    const address: string = (resort as any).location?.address || '';
    if (Array.isArray(coords) && coords.length >= 2) {
      // Backend schema stores [lng, lat]
      setLocLng(typeof coords[0] === 'number' ? coords[0] : parseFloat(coords[0]));
      setLocLat(typeof coords[1] === 'number' ? coords[1] : parseFloat(coords[1]));
    } else if (coords && typeof coords === 'object') {
      if (coords.longitude != null && coords.latitude != null) {
        setLocLng(typeof coords.longitude === 'number' ? coords.longitude : parseFloat(coords.longitude));
        setLocLat(typeof coords.latitude === 'number' ? coords.latitude : parseFloat(coords.latitude));
      }
    } else {
      setLocLat('');
      setLocLng('');
    }
    setLocAddress(address);
  };

  // Amenities handlers
  const addAmenity = () => {
    setAmenities([...amenities, { name: '', icon: '' }]);
  };

  const removeAmenity = (index: number) => {
    if (amenities.length > 1) {
      setAmenities(amenities.filter((_, i) => i !== index));
    }
  };

  const updateAmenity = (index: number, field: 'name' | 'icon', value: string) => {
    const updated = amenities.map((amenity, i) =>
      i === index ? { ...amenity, [field]: value } : amenity
    );
    setAmenities(updated);
  };

  // Banner/Gallery handlers
  const onBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setBannerFile(e.target.files[0]);
    }
  };

  const onGalleryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length) {
      setGalleryFiles(prev => [...prev, ...files]);
    }
    // reset input to allow re-selecting the same file(s)
    e.currentTarget.value = '';
  };

  const removeGalleryFile = (index: number) => {
    setGalleryFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingGalleryImage = (index: number) => {
    setExistingGallery(prev => {
      const imageToRemove = prev[index];
      const newGallery = prev.filter((_, i) => i !== index);
      console.log('🗑️ Removing existing gallery image:', imageToRemove);
      console.log('📸 Remaining images:', newGallery.length);
      return newGallery;
    });
  };

  // Testimonials handlers
  const addTestimonial = () => {
    setTestimonials([...testimonials, { customerName: '', rating: 5, comment: '', date: new Date().toISOString().split('T')[0] }]);
  };

  const removeTestimonial = (index: number) => {
    if (testimonials.length > 1) {
      setTestimonials(testimonials.filter((_, i) => i !== index));
    }
  };

  const updateTestimonial = (index: number, field: keyof Testimonial, value: string | number) => {
    const updated = testimonials.map((testimonial, i) =>
      i === index ? { ...testimonial, [field]: value } : testimonial
    );
    setTestimonials(updated);
  };

  const handleToggleStatus = async (id: string) => {
    try {
      const resort = resorts.find(r => r._id === id);
      if (!resort) return;
      
      const newStatus = !resort.isActive;
      console.log(`🔄 Toggling resort status: ${resort.name} -> ${newStatus ? 'Active' : 'Inactive'}`);
      
      // Optimistic update
      setResorts(resorts.map(r => 
        r._id === id ? { ...r, isActive: newStatus } : r
      ));
      
      // Make API call to update in database
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/resorts/${id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ isActive: newStatus })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to update resort status');
      }
      
      const updatedResort = await response.json();
      console.log('✅ Resort status updated successfully:', updatedResort);
      
    } catch (error: any) {
      console.error('❌ Error updating resort status:', error);
      
      // Revert optimistic update on error
      setResorts(resorts.map(r => 
        r._id === id ? { ...r, isActive: !r.isActive } : r
      ));
      
      alert(`Failed to update resort status: ${error.message}`);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this resort?')) {
      setResorts(resorts.filter(resort => resort._id !== id));
    }
  };

  const handleCloneResort = async (originalResort: Resort) => {
    try {
      // Enhanced confirmation dialog
      const cloneName = prompt(
        `📋 Clone Resort: ${originalResort.name}\n\n` +
        `Enter a name for the cloned resort:\n` +
        `(The clone will start as INACTIVE so you can edit it before making it live)`,
        `${originalResort.name} (Copy)`
      );
      
      if (!cloneName || cloneName.trim() === '') {
        return; // User cancelled or entered empty name
      }
      
      // Show progress
      console.log('📋 Starting resort clone process...');
      
      // Create clone data
      const cloneData = {
        name: cloneName,
        destination: originalResort.destination._id,
        about: originalResort.about,
        resortCode: '', // Will be auto-generated
        location: {
          address: originalResort.location?.address || '',
          coordinates: originalResort.location?.coordinates || [0, 0]
        },
        contactInfo: {
          phone: originalResort.contactInfo?.phone || '',
          email: originalResort.contactInfo?.email || '',
          website: originalResort.contactInfo?.website || ''
        },
        amenities: [...(originalResort.amenities || [])],
        testimonials: [...(originalResort.testimonials || [])],
        isActive: false, // Start cloned resort as inactive
        isPartnerHotel: originalResort.isPartnerHotel
        // Note: bannerImage and gallery will be copied by backend
      };
      
      console.log('📋 Cloning resort:', originalResort.name, '-> Clone data:', cloneData);
      
      // Make API call to clone resort
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/resorts/${originalResort._id}/clone`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(cloneData)
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to clone resort');
      }
      
      const result = await response.json();
      console.log('✅ Resort cloned successfully:', result);
      
      // Refresh the resorts list
      await fetchResorts();
      
      // Show success message with more details
      const clonedResortInfo = result.resort;
      alert(
        `✅ Resort Cloned Successfully!\n\n` +
        `🏨 Original: ${originalResort.name}\n` +
        `🆕 Clone: ${clonedResortInfo.name}\n` +
        `🔑 Resort Code: ${clonedResortInfo.resortCode}\n\n` +
        `🟡 Status: INACTIVE (ready for editing)\n\n` +
        `What was cloned:\n` +
        `• All resort details & amenities\n` +
        `• All images (banner & gallery)\n` +
        `• All testimonials\n` +
        `• Location & contact info\n\n` +
        `You can now edit the clone and activate it when ready!`
      );
      
    } catch (error: any) {
      console.error('❌ Error cloning resort:', error);
      
      // Show more user-friendly error messages
      let errorMessage = 'Unknown error occurred';
      
      if (error.message.includes('validation failed')) {
        errorMessage = 'Resort data validation failed. This may be due to missing or invalid data in the original resort.';
      } else if (error.message.includes('already exists')) {
        errorMessage = 'A resort with this name already exists. Please try a different name.';
      } else if (error.message.includes('Failed to clone resort')) {
        errorMessage = error.message;
      } else {
        errorMessage = error.message || 'Failed to clone resort';
      }
      
      alert(
        `❌ Failed to Clone Resort\n\n` +
        `Error: ${errorMessage}\n\n` +
        `Please try again or contact support if the problem persists.`
      );
    }
  };

  // Debug function to check resort health
  const checkResortHealth = async (resortId: string, resortName: string) => {
    try {
      console.log('🌡️ Checking health for resort:', resortName);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/resorts/${resortId}/health`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const healthData = await response.json();
      console.log('🌡️ Health check result:', healthData);
      
      if (healthData.healthy) {
        alert(
          `✅ Resort "${resortName}" is healthy!\n\n` +
          `• Data structure: Valid\n` +
          `• Amenities: ${healthData.dataStructure.amenitiesCount}\n` +
          `• Testimonials: ${healthData.dataStructure.testimonialsCount}\n` +
          `• Banner image: ${healthData.dataStructure.hasImages.banner ? 'Yes' : 'No'}\n` +
          `• Gallery: ${healthData.dataStructure.hasImages.gallery ? 'Yes' : 'No'}`
        );
      } else {
        alert(
          `⚠️ Resort "${resortName}" has issues:\n\n` +
          healthData.issues.map((issue: string) => `• ${issue}`).join('\n') +
          '\n\nThis may explain why editing fails.'
        );
      }
      
    } catch (error: any) {
      console.error('❌ Health check failed:', error);
      alert(`❌ Health check failed: ${error.message}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>Resorts - Bali Getaway Admin</title>
      </Helmet>

      <div className="space-y-8">
        {/* Modern Header */}
        <div className="bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50 rounded-2xl p-8">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <BuildingOfficeIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-700 to-orange-700 bg-clip-text text-transparent">Resort Management</h1>
                  <p className="text-gray-600 text-lg">Create and manage luxury resort partners</p>
                </div>
              </div>
            </div>
            <button 
              onClick={() => navigate('/admin/resorts/create')}
              className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Resort
            </button>
          </div>
        </div>

        {/* Modern Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white/80 backdrop-blur rounded-2xl shadow-xl border border-orange-200/50 p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <BuildingOfficeIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">{resorts.length}</p>
                <p className="text-sm font-medium text-gray-600">Total Resorts</p>
              </div>
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur rounded-2xl shadow-xl border border-orange-200/50 p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <EyeIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                  {resorts.filter(r => r.isActive).length}
                </p>
                <p className="text-sm font-medium text-gray-600">Active Resorts</p>
              </div>
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur rounded-2xl shadow-xl border border-orange-200/50 p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <StarIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-700 bg-clip-text text-transparent">
                  {resorts.filter(r => r.isPartnerHotel).length}
                </p>
                <p className="text-sm font-medium text-gray-600">Partner Resorts</p>
              </div>
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur rounded-2xl shadow-xl border border-orange-200/50 p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <MapPinIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-transparent">
                  {new Set(resorts.map(r => r.destination._id)).size}
                </p>
                <p className="text-sm font-medium text-gray-600">Destinations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Filters */}
        <div className="bg-white/80 backdrop-blur rounded-2xl shadow-xl border border-orange-200/50 p-6">
          <div className="flex items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">🔍 Filter Resorts</h3>
          </div>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Status Filter
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
                className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-orange-500/20 focus:ring-4 transition-all bg-white/90"
              >
                <option value="all">🏨 All Statuses</option>
                <option value="active">✅ Active Only</option>
                <option value="inactive">⏸️ Inactive Only</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Destination Filter
              </label>
              <select
                value={filterDestination}
                onChange={(e) => setFilterDestination(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:ring-orange-500/20 focus:ring-4 transition-all bg-white/90"
              >
                <option value="all">🌍 All Destinations</option>
                {destinations.map(dest => (
                  <option key={dest._id} value={dest._id}>📍 {dest.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Modern Resorts Grid */}
        <div className="bg-white/80 backdrop-blur shadow-xl rounded-2xl border border-orange-200/50">
          <div className="px-8 py-6 border-b border-orange-200/50 bg-gradient-to-r from-yellow-50 to-orange-50">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold bg-gradient-to-r from-yellow-700 to-orange-700 bg-clip-text text-transparent">
                🏨 All Resorts ({filteredResorts.length})
              </h2>
              <div className="text-sm text-gray-600">
                {resorts.filter(r => r.isActive).length} active • {resorts.filter(r => !r.isActive).length} inactive
              </div>
            </div>
          </div>
          
          {filteredResorts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <BuildingOfficeIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No resorts found</h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                {resorts.length === 0 
                  ? 'Ready to add your first luxury resort? Let\'s get started!' 
                  : 'Try adjusting your filters to find the resorts you\'re looking for.'}
              </p>
              {resorts.length === 0 && (
                <button onClick={handleAdd} className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Your First Resort
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
              {filteredResorts.map((resort) => {
                const slug = resort.slug || resort.name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
                const statusLabel = resort.isActive ? 'Deactivate' : 'Activate';
                const statusClasses = resort.isActive
                  ? 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
                  : 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-200';
                const statusIcon = resort.isActive ? (
                  <XCircleIcon className="h-3.5 w-3.5 mr-1.5" />
                ) : (
                  <CheckCircleIcon className="h-3.5 w-3.5 mr-1.5" />
                );

                return (
                  <div key={resort._id} className="bg-white/90 backdrop-blur rounded-2xl border border-orange-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                  {/* Resort Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      className="w-full h-full object-cover"
                      src={resort.bannerImage}
                      alt={resort.name}
                    />
                    <div className="absolute top-3 right-3">
                      <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                        resort.isActive 
                          ? 'bg-green-500 text-white' 
                          : 'bg-red-500 text-white'
                      }`}>
                        {resort.isActive ? '✅ Active' : '⏸️ Inactive'}
                      </span>
                    </div>
                    <div className="absolute top-3 left-3">
                      {resort.isPartnerHotel && (
                        <span className="inline-flex px-3 py-1 text-xs font-bold rounded-full bg-purple-500 text-white">
                          ⭐ Partner
                        </span>
                      )}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-3 text-white">
                      <h3 className="text-lg font-bold mb-1">{resort.name}</h3>
                      <p className="text-sm opacity-90">📍 {resort.destination.name}</p>
                    </div>
                  </div>

                  {/* Resort Info */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Code:</span> {resort.resortCode}
                      </div>
                      <div className="flex items-center">
                        <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                        <span className="text-sm font-bold text-gray-900">
                          {resort.averageRating || 'N/A'}
                        </span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-xs text-gray-500 mb-1">📦 Packages Linked</div>
                      <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-500 to-orange-500 bg-clip-text text-transparent">
                        {resort.packageCount}
                      </div>
                    </div>

                    {/* Action Buttons - Modern Layout */}
                    <div className="mt-6 pt-4 border-t border-orange-100">
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                          onClick={() => navigate(`/admin/resorts/edit/${resort._id}`)}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          <PencilIcon className="h-4 w-4 mr-2" />
                          Edit Resort
                        </button>
                        <Link
                          to={`/resorts/${slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          <EyeIcon className="h-4 w-4 mr-2" />
                          View Public Page
                        </Link>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                        <button
                          onClick={() => handleCloneResort(resort)}
                          className="inline-flex items-center justify-center px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-lg text-xs font-medium transition-all duration-200"
                          title="Clone resort"
                        >
                          <DocumentDuplicateIcon className="h-3.5 w-3.5 mr-1.5" />
                          Clone
                        </button>
                        <button
                          onClick={() => handleToggleStatus(resort._id)}
                          className={`inline-flex items-center justify-center px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${statusClasses}`}
                          title={`${statusLabel} resort`}
                        >
                          {statusIcon}
                          {statusLabel}
                        </button>
                        <button
                          onClick={() => checkResortHealth(resort._id, resort.name)}
                          className="inline-flex items-center justify-center px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg text-xs font-medium transition-all duration-200"
                          title="Run quick health check"
                        >
                          <SparklesIcon className="h-3.5 w-3.5 mr-1.5" />
                          Health
                        </button>
                      </div>

                      {resort.packageCount > 0 ? (
                        <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 text-center">
                          ⚠️ Cannot delete: {resort.packageCount} packages linked
                        </div>
                      ) : (
                        <button
                          onClick={() => handleDelete(resort._id)}
                          className="w-full mt-3 inline-flex items-center justify-center px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-medium transition-all duration-200"
                          title="Delete resort"
                        >
                          <TrashIcon className="h-3.5 w-3.5 mr-1.5" />
                          Delete Resort
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal Placeholder */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingResort ? 'Edit Resort' : 'Add New Resort'}
              </h3>
              
              <form id="resort-form" className="space-y-6" onSubmit={handleSave}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Resort Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      className="form-input"
                      placeholder="Enter resort name"
                      defaultValue={editingResort?.name || ''}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Resort Code
                    </label>
                    <input
                      type="text"
                      name="resortCode"
                      className="form-input"
                      placeholder="Enter resort code"
                      defaultValue={editingResort?.resortCode || ''}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Destination
                    </label>
                    <select
                      name="destination"
                      className="form-input"
                      defaultValue={editingResort?.destination?._id || ''}
                      required
                    >
                      <option value="">Select destination</option>
                      {destinations.map(dest => (
                        <option key={dest._id} value={dest._id}>{dest.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rating
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="1"
                      max="5"
                      className="form-input"
                      placeholder="4.5"
                      defaultValue={editingResort?.averageRating || ''}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner Image Upload
                  </label>
                  <input
                    type="file"
                    name="bannerImage"
                    accept="image/*"
                    className="form-input"
                    onChange={onBannerChange}
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload main banner image for the resort</p>
                  {(bannerFile || editingResort?.bannerImage) && (
                    <div className="mt-2">
                      <img
                        src={bannerFile ? URL.createObjectURL(bannerFile) : editingResort?.bannerImage}
                        alt="Banner preview"
                        className="h-20 w-32 object-cover rounded border"
                      />
                      <p className="text-xs text-gray-500">{bannerFile ? 'Selected banner image preview' : 'Current banner image'}</p>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    About Resort
                  </label>
                  <textarea
                    name="about"
                    className="form-input"
                    rows={4}
                    placeholder="Enter resort description"
                    defaultValue={editingResort?.about || ''}
                  />
                </div>
                
                {/* Location & Map */}
                <div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Location & Map
                    </label>
                    <p className="text-xs text-gray-500">Use Google Places to search the address, or adjust coordinates manually.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                      <input
                        type="text"
                        ref={addressInputRef}
                        className="form-input"
                        placeholder="Start typing address..."
                        value={locAddress}
                        onChange={(e) => setLocAddress(e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
                        <input
                          type="number"
                          step="0.000001"
                          className="form-input"
                          placeholder="-8.409518"
                          value={locLat as any}
                          onChange={(e) => setLocLat(e.target.value === '' ? '' : parseFloat(e.target.value))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
                        <input
                          type="number"
                          step="0.000001"
                          className="form-input"
                          placeholder="115.188919"
                          value={locLng as any}
                          onChange={(e) => setLocLng(e.target.value === '' ? '' : parseFloat(e.target.value))}
                        />
                      </div>
                    </div>
                  </div>
                  {(typeof locLat === 'number' && typeof locLng === 'number' && !Number.isNaN(locLat) && !Number.isNaN(locLng)) && (
                    <div className="mt-3 h-56 w-full overflow-hidden rounded border">
                      <iframe
                        title="Preview Map"
                        src={`https://maps.google.com/maps?q=${locLat},${locLng}&z=14&output=embed`}
                        className="w-full h-full border-0"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  )}
                </div>
                
                {/* Amenities Repeater */}
                <div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Amenities
                    </label>
                  </div>
                  <div className="space-y-3">
                    {amenities.map((amenity, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 border border-gray-200 rounded">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Amenity Name
                          </label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="e.g., Infinity Pool"
                            value={amenity.name}
                            onChange={(e) => updateAmenity(index, 'name', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Icon/Image Upload
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="file"
                              accept="image/*"
                              className="form-input flex-1"
                              onChange={(e) => {
                                // Handle file upload and set icon URL
                                if (e.target.files && e.target.files[0]) {
                                  // In real implementation, upload to server and get URL
                                  updateAmenity(index, 'icon', URL.createObjectURL(e.target.files[0]));
                                }
                              }}
                            />
                            {amenities.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeAmenity(index)}
                                className="text-red-600 hover:text-red-800 px-2"
                                title="Remove amenity"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={addAmenity}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      + Add Amenity
                    </button>
                  </div>
                </div>
                
                {/* Gallery Images */}
                <div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Gallery Images
                    </label>
                  </div>
                  
                  {/* Existing Gallery Images */}
                  {existingGallery.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-2">Current Images ({existingGallery.length})</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {existingGallery.map((imageUrl, index) => {
                          const displayUrl = imageUrl.includes('cloudinary.com') || imageUrl.startsWith('http') 
                            ? imageUrl 
                            : `${window.location.port === '3000' ? 'http://localhost:5000' : ''}${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
                          
                          return (
                            <div key={imageUrl} className="relative group border border-green-200 rounded p-1 bg-green-50">
                              <img
                                src={displayUrl}
                                alt={`Existing ${index + 1}`}
                                className="h-24 w-full object-cover rounded"
                                onError={(e) => {
                                  console.error('Failed to load image:', imageUrl);
                                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23999" dy=".3em"%3EError%3C/text%3E%3C/svg%3E';
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => removeExistingGalleryImage(index)}
                                className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-0.5 rounded opacity-90 hover:opacity-100"
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* New Gallery Files */}
                  <input
                    ref={galleryInputRef}
                    id="gallery-input"
                    type="file"
                    name="gallery"
                    accept="image/*"
                    multiple
                    className="form-input"
                    onChange={onGalleryChange}
                  />
                  {galleryFiles.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-2">New Images to Upload ({galleryFiles.length})</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {galleryFiles.map((file, index) => (
                          <div key={`${file.name}-${file.size}-${index}`} className="relative group border border-blue-200 rounded p-1 bg-blue-50">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`New ${index + 1}`}
                              className="h-24 w-full object-cover rounded"
                            />
                            <button
                              type="button"
                              onClick={() => removeGalleryFile(index)}
                              className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-0.5 rounded opacity-90 hover:opacity-100"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => galleryInputRef.current?.click()}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      + Add Images
                    </button>
                  </div>
                </div>
                
                {/* Testimonials Repeater */}
                <div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Testimonials/Reviews
                    </label>
                  </div>
                  <div className="space-y-4">
                    {testimonials.map((testimonial, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Customer Name
                            </label>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="e.g., John Smith"
                              value={testimonial.customerName}
                              onChange={(e) => updateTestimonial(index, 'customerName', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Rating (1-5)
                            </label>
                            <select
                              className="form-input"
                              value={testimonial.rating}
                              onChange={(e) => updateTestimonial(index, 'rating', parseInt(e.target.value))}
                            >
                              <option value={5}>⭐⭐⭐⭐⭐ (5)</option>
                              <option value={4}>⭐⭐⭐⭐ (4)</option>
                              <option value={3}>⭐⭐⭐ (3)</option>
                              <option value={2}>⭐⭐ (2)</option>
                              <option value={1}>⭐ (1)</option>
                            </select>
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Review Content
                          </label>
                          <textarea
                            className="form-input"
                            rows={3}
                            placeholder="Enter the customer's review..."
                            value={testimonial.comment}
                            onChange={(e) => updateTestimonial(index, 'comment', e.target.value)}
                          />
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Review Date
                            </label>
                            <input
                              type="date"
                              className="form-input"
                              value={testimonial.date}
                              onChange={(e) => updateTestimonial(index, 'date', e.target.value)}
                            />
                          </div>
                          {testimonials.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeTestimonial(index)}
                              className="text-red-600 hover:text-red-800 mt-5 px-2 py-1 border border-red-300 rounded"
                              title="Remove testimonial"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={addTestimonial}
                      className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      + Add Testimonial
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      defaultChecked={editingResort?.isActive ?? true}
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                      Active resort
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPartner"
                      name="isPartnerHotel"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      defaultChecked={editingResort?.isPartnerHotel ?? false}
                    />
                    <label htmlFor="isPartner" className="ml-2 block text-sm text-gray-700">
                      Partner resort
                    </label>
                  </div>
                </div>
              </form>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="resort-form"
                  className="btn-primary"
                >
                  {editingResort ? 'Update' : 'Create'} Resort
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminResorts;
